import { Box, useMediaQuery, ButtonGroup } from '@mui/material';
import { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { BuildCircle, AttachMoney, Visibility, DesignServices } from '@mui/icons-material';
import { IClientBuild, IResponse, ISortDirection } from '../../../../models';
import { useQuery } from 'react-query';
import { useFlags } from '../../../../hooks';
import { getClientBuilds } from '../../../../fetch';
import { Button, MobileTable, Pagination, Table } from '../../../../components';
import { BuySpecModal } from './buy-spec-modal';
import { AddBuildModal } from './add-build-modal';
import { formatShortFriendlyDateWithTime } from '../../../../helpers';
const { LegacySystem } = require('../../../../buildSettings.json');

export const BuildsTab = ({ personId }) => {
  const history = useHistory();
  const featureFlags = useFlags();

  const isMobile = useMediaQuery('(max-width: 960px)');

  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [isAddBuildModalOpen, setIsAddBuildModalOpen] = useState(false);
  const [isShowingBuySpec, setIsShowingBuySpec] = useState(false);
  const [isTestBuild, setIsTestBuild] = useState(false);
  const [selectedSort, setSelectedSort] = useState<string>('ContractStarted');
  const [sortDirection, setSortDirection] = useState<{
    NeighborhoodName?: ISortDirection;
    LotNumber?: ISortDirection;
    ContractStarted?: ISortDirection;
  }>({
    ContractStarted: 'Desc'
  });

  const { isLoading, data: buildsData } = useQuery<IResponse<IClientBuild[]>, Error>(
    ['getBuilds', page, perPage, selectedSort, sortDirection],
    () =>
      getClientBuilds(personId, {
        page: page + 1,
        perPage,
        personId: personId,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string
      }),
    {
      onSuccess: data => {
        setRecordCount(data.totalRecordCount);
      },
      notifyOnChangeProps: 'tracked'
    }
  );

  const handleView = buildId => {
    if (!featureFlags.clientBuildCreation) {
      window.location.href = `${LegacySystem}/Portals/Sales/BuildOptions.aspx?clientId=${personId}&buildId=${buildId}`;
    } else {
      history.push(
        `/sales/contracting/build-options/${buildId}?redirect=/sales/contracting/purchasers/${personId}/builds`
      );
    }
  };
  const data = useMemo(() => buildsData?.records ?? [], [buildsData]);
  const columns = useMemo(() => {
    const handleClickColumn = (column: string) => {
      setSelectedSort(column);
      setSortDirection({
        ...sortDirection,
        [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
      });
    };
    return [
      {
        Header: 'Neighborhood',
        accessor: 'neighborhoodName',
        isServerSorted: selectedSort === 'NeighborhoodName',
        isServerSortedDesc: sortDirection.NeighborhoodName === 'Desc',
        handleClickColumn: () => handleClickColumn('NeighborhoodName')
      },
      {
        Header: 'Lot',
        accessor: 'lotNumber',
        isServerSorted: selectedSort === 'LotNumber',
        isServerSortedDesc: sortDirection.LotNumber === 'Desc',
        handleClickColumn: () => handleClickColumn('LotNumber')
      },
      {
        Header: 'Plan',
        accessor: 'plan',
        sort: false
      },
      {
        Header: 'Status',
        accessor: 'status',
        sort: false
      },
      {
        Header: 'Contract Started',
        accessor: 'contractStarted',
        isServerSorted: selectedSort === 'ContractStarted',
        isServerSortedDesc: sortDirection?.ContractStarted === 'Desc',
        handleClickColumn: () => handleClickColumn('ContractStarted'),

        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IClientBuild } };
        }) => {
          return (
            <>
              {original.contractStarted
                ? formatShortFriendlyDateWithTime(original.contractStarted)
                : null}
            </>
          );
        }
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        sort: false,

        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IClientBuild } };
        }) => {
          return (
            <Box display="flex">
              <Button
                id={`view-build-${original.buildId}`}
                type="button"
                color="primary"
                startIcon={<Visibility />}
                onClick={() => handleView(original.buildId)}
              >
                View
              </Button>
            </Box>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSort, sortDirection, history, personId]);

  return (
    <>
      <Box marginTop="-1rem" display="flex" justifyContent="flex-end">
        <ButtonGroup>
          {featureFlags.salesTestBuild && (
            <Button
              id="create-test-build"
              type="button"
              color="secondary"
              variant="contained"
              startIcon={<DesignServices />}
              onClick={() => {
                setIsTestBuild(true);
                setIsAddBuildModalOpen(true);
              }}
            >
              Test Build
            </Button>
          )}
          <Button
            id="create-build"
            type="button"
            color="secondary"
            variant="contained"
            startIcon={<BuildCircle />}
            onClick={() => {
              if (!featureFlags.clientBuildCreation) {
                window.location.href = `${LegacySystem}/Portals/Sales/ManageBuilds.aspx?clientId=${personId}`;
              } else {
                setIsTestBuild(false);
                setIsAddBuildModalOpen(true);
              }
            }}
          >
            New Build
          </Button>
          <Button
            id="buy-spec"
            type="button"
            color="secondary"
            variant="contained"
            startIcon={<AttachMoney />}
            onClick={() => {
              if (!featureFlags.clientBuildCreation) {
                window.location.href = `${LegacySystem}/Portals/Sales/BuySpec.aspx?clientId=${personId}`;
              } else {
                setIsShowingBuySpec(true);
              }
            }}
          >
            Buy Spec
          </Button>
        </ButtonGroup>
      </Box>
      <Table
        stickyHeaders
        columns={columns}
        data={data}
        isLoading={isLoading}
        hidePagination
        mobileProps={{
          fields: columns
            .filter(c => c.Header)
            .map(c => ({
              name: c.Header,
              accessor: c.accessor,
              Cell: c?.Cell
            })),
          handleView: (original: IClientBuild) => handleView(original.buildId)
        }}
        ResponsiveComponent={isMobile ? MobileTable : undefined}
      />
      {!isLoading && (
        <Pagination
          page={page}
          count={recordCount}
          rowsPerPage={perPage}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
        />
      )}
      <AddBuildModal
        open={isAddBuildModalOpen}
        onClose={() => setIsAddBuildModalOpen(false)}
        personId={personId}
        isTestBuild={isTestBuild}
      />
      <BuySpecModal
        open={isShowingBuySpec}
        onClose={() => {
          setIsShowingBuySpec(false);
        }}
        personId={personId}
      />
    </>
  );
};
